import { useQuery } from '@tanstack/react-query'
import store from 'stores/store'
import { milliseconds } from 'date-fns'
import { fetchBirdeyeTokenOverview } from 'hooks/useBirdeyeTokenOverview'
import { JupiterPriceResponse } from 'types/jupiter'
import { fetchJupiterTokenInfo } from 'hooks/useJupiterTokenInfo'
import { JUPITER_PRICE_API, jupiterFetch } from 'utils/jupiter'

export function makeJupiterPriceUrlForMints(mints: string[]) {
  const priceUrl = new URL(JUPITER_PRICE_API)
  priceUrl.searchParams.append('ids', mints.join(','))

  return priceUrl
}

async function fetchSwapTokenInfo(inputMint: string, outputMint: string) {
  const [inputData, outputData] = await Promise.all([
    fetchBirdeyeTokenOverview(inputMint),
    fetchBirdeyeTokenOverview(outputMint),
  ])

  if (inputData && outputData) {
    return { inputData, outputData }
  } else {
    return await fetchFallbackSwapTokenInfo(inputMint, outputMint)
  }
}

async function fetchFallbackSwapTokenInfo(
  inputMint: string,
  outputMint: string,
) {
  try {
    const [inputToken, outputToken, priceResponse] = await Promise.all([
      fetchJupiterTokenInfo(inputMint),
      fetchJupiterTokenInfo(outputMint),
      jupiterFetch(makeJupiterPriceUrlForMints([inputMint, outputMint])).then(
        (res) => res.json() as Promise<JupiterPriceResponse>,
      ),
    ])

    if (
      inputToken?.address &&
      outputToken?.address &&
      priceResponse?.data?.[inputMint] &&
      priceResponse?.data?.[outputMint]
    ) {
      return {
        inputData: {
          ...inputToken,
          price: priceResponse.data[inputMint].usdPrice,
        },
        outputData: {
          ...outputToken,
          price: priceResponse.data[outputMint].usdPrice,
        },
      }
    } else {
      return null
    }
  } catch (e) {
    console.error(
      `failed to fetch fallback swap token info: inputMint=${inputMint}, outputMint=${outputMint}`,
      e,
    )
    return null
  }
}

function useBirdeyeSwapData() {
  const inputMint = store((s) => s.swap.inputToken.mint)
  const outputMint = store((s) => s.swap.outputToken.mint)
  const { data: swapData } = useQuery({
    queryKey: ['swap-token-info', inputMint, outputMint],
    queryFn: () => fetchSwapTokenInfo(inputMint, outputMint),
    cacheTime: milliseconds({ seconds: 60 }),
    staleTime: milliseconds({ seconds: 3 }),
    enabled: !!(inputMint && outputMint),
    refetchOnWindowFocus: false,
  })

  const inputTokenInfo = swapData?.inputData
  const outputTokenInfo = swapData?.outputData
  const inputCoingeckoId = swapData?.inputData?.extensions?.coingeckoId
  const outputCoingeckoId = swapData?.outputData?.extensions?.coingeckoId

  return {
    inputTokenInfo,
    inputCoingeckoId,
    outputTokenInfo,
    outputCoingeckoId,
  }
}

export default useBirdeyeSwapData
